import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';

const ReassignmentModal = ({ targetUser, activeUsers, onConfirm, onCancel }) => {
    const [loading, setLoading] = useState(true);
    const [openTasks, setOpenTasks] = useState([]);
    const [openOpps, setOpenOpps] = useState([]);
    const [reassignTo, setReassignTo] = useState('');
    const [skipReassignment, setSkipReassignment] = useState(false);

    const targetUid = targetUser?.uid || targetUser?.id;
    const targetDisplayName = targetUser?.displayName || `${targetUser?.firstName || ''} ${targetUser?.lastName || ''}`.trim();

    // Eligible users: active (not disabled), not the target user
    const eligibleUsers = activeUsers
        .filter(u => (u.uid || u.id) !== targetUid && u.role !== 'disabled')
        .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));

    useEffect(() => {
        const fetchAssignments = async () => {
            setLoading(true);
            try {
                // Query open tasks assigned to this user (by uid)
                const tasksQuery = query(
                    collection(db, collectionPath('tasks')),
                    where('uid', '==', targetUid),
                    where('status', '==', 'open')
                );
                const tasksSnap = await getDocs(tasksQuery);
                setOpenTasks(tasksSnap.docs.map(d => ({ id: d.id, ...d.data() })));

                // Query open opportunities assigned to this user (by uid)
                const oppsQuery = query(
                    collection(db, collectionPath('opportunities')),
                    where('uid', '==', targetUid)
                );
                const oppsSnap = await getDocs(oppsQuery);
                // Filter out closed opportunities client-side
                const openOnly = oppsSnap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(o => o.stage !== 'Closed Won' && o.stage !== 'Closed Lost');
                setOpenOpps(openOnly);
            } catch (err) {
                console.error('Error fetching assignments:', err);
            } finally {
                setLoading(false);
            }
        };
        if (targetUid) fetchAssignments();
    }, [targetUid]);

    const totalItems = openTasks.length + openOpps.length;
    const canProceed = skipReassignment || reassignTo || totalItems === 0;

    const handleConfirm = () => {
        const selectedUser = eligibleUsers.find(u => (u.uid || u.id) === reassignTo);
        onConfirm({
            reassignToUid: skipReassignment ? null : reassignTo,
            reassignToName: skipReassignment ? null : (selectedUser?.displayName || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim()),
            tasks: openTasks,
            opportunities: openOpps,
            skipReassignment
        });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                    <div className="flex items-center gap-3">
                        <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        <span className="text-gray-600">Checking assignments for {targetDisplayName}...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    Deactivate {targetDisplayName}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                    This will disable their account and prevent login.
                </p>

                {totalItems === 0 ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <p className="text-green-800 text-sm font-medium">
                            No open tasks or opportunities assigned to this user.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                            <p className="text-amber-800 text-sm font-medium mb-2">
                                This user has active assignments:
                            </p>
                            <ul className="text-sm text-amber-700 space-y-1">
                                {openTasks.length > 0 && (
                                    <li className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-amber-200 text-amber-800 rounded-full text-xs font-bold">
                                            {openTasks.length}
                                        </span>
                                        open task{openTasks.length !== 1 ? 's' : ''}
                                    </li>
                                )}
                                {openOpps.length > 0 && (
                                    <li className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-amber-200 text-amber-800 rounded-full text-xs font-bold">
                                            {openOpps.length}
                                        </span>
                                        open opportunit{openOpps.length !== 1 ? 'ies' : 'y'}
                                    </li>
                                )}
                            </ul>
                        </div>

                        {/* Task details */}
                        {openTasks.length > 0 && openTasks.length <= 10 && (
                            <details className="mb-3">
                                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                    View tasks
                                </summary>
                                <ul className="mt-1 text-xs text-gray-600 space-y-1 pl-4 max-h-32 overflow-y-auto">
                                    {openTasks.map(t => (
                                        <li key={t.id} className="truncate">
                                            &bull; {t.title || 'Untitled'} {t.dueDate ? `(due ${typeof t.dueDate?.toDate === 'function' ? t.dueDate.toDate().toLocaleDateString() : new Date(t.dueDate).toLocaleDateString()})` : ''}
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}

                        {/* Opportunity details */}
                        {openOpps.length > 0 && openOpps.length <= 10 && (
                            <details className="mb-3">
                                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                    View opportunities
                                </summary>
                                <ul className="mt-1 text-xs text-gray-600 space-y-1 pl-4 max-h-32 overflow-y-auto">
                                    {openOpps.map(o => (
                                        <li key={o.id} className="truncate">
                                            &bull; {o.opportunityNumber || o.id} — {o.customerName || 'Unknown'} ({o.stage || 'N/A'})
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Reassign to
                            </label>
                            <select
                                value={skipReassignment ? '__skip__' : reassignTo}
                                onChange={(e) => {
                                    if (e.target.value === '__skip__') {
                                        setSkipReassignment(true);
                                        setReassignTo('');
                                    } else {
                                        setSkipReassignment(false);
                                        setReassignTo(e.target.value);
                                    }
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">-- Select a user --</option>
                                <option value="__skip__">Skip reassignment (leave as-is)</option>
                                {eligibleUsers.map(u => (
                                    <option key={u.uid || u.id} value={u.uid || u.id}>
                                        {u.displayName || `${u.firstName || ''} ${u.lastName || ''}`.trim()}
                                    </option>
                                ))}
                            </select>
                            {skipReassignment && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Tasks and opportunities will remain assigned to the deactivated user.
                                </p>
                            )}
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!canProceed}
                        className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {totalItems === 0 ? 'Deactivate User' : (skipReassignment ? 'Deactivate Without Reassigning' : 'Reassign & Deactivate')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReassignmentModal;
